import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useAccountStore, useEffectivePlan } from '../store/accountStore';
import { useBuilderStore } from '../store/builderStore';
import { UserLayout } from '../components/User/UserLayout';
import EditSlugModal from '../components/User/EditSlugModal';
import { EmailVerificationBanner } from '../components/EmailVerificationBanner';
import { SubscriptionLifecycleBanner } from '../components/Common/SubscriptionLifecycleBanner';
import { 
  Plus, 
  FileText, 
  Eye, 
  Edit, 
  Trash2, 
  Copy,
  BarChart,
  TrendingUp,
  Crown,
  Lock,
  Link as LinkIcon,
  ExternalLink,
  Disc,
  Users,
  Globe,
  ShieldCheck,
  Pencil,
  Check,
  X,
  Sparkles,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { getMaxPages, normalizeTier, tierLabel, hasFeature } from '../constants/planLimits';
import { usePlanUsage } from '../hooks/usePlanUsage';
import type { PlanUsageState } from '../hooks/usePlanUsage';
import type { TFunction } from 'i18next';
import type { NavigateFunction } from 'react-router-dom';

export const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('dashboard');
  const { user } = useAuthStore();
  const planUsage = usePlanUsage();
  // When acting inside another owner's workspace (team member), plan +
  // limits belong to the WORKSPACE OWNER, not the logged-in member. The
  // server resolves this via the X-Account-Id header and surfaces the
  // owner's plan through usePlanUsage (/pages/limits). We fall back to
  // the member's own subscription only until that account-scoped fetch
  // lands, or when they're on their own account.
  const activeAccountId = useAccountStore((s) => s.activeAccountId);
  const accounts = useAccountStore((s) => s.accounts);
  const setActiveAccount = useAccountStore((s) => s.setActiveAccount);
  const workspacePlan = useEffectivePlan();
  const inTeamWorkspace = !!activeAccountId && activeAccountId !== user?.id;
  // Prefer /pages/limits (account-scoped), else workspace owner plan.
  const effectiveTier = planUsage.plan ?? normalizeTier(workspacePlan);
  // Workspaces the user joined as a team member (excludes their own
  // account). Surfaced as quick-switch cards on the dashboard.
  const joinedTeams = accounts.filter((a) => !a.isSelf);

  const switchToWorkspace = (id: string) => {
    if (id === (activeAccountId || user?.id)) return;
    setActiveAccount(id);
    // Hard reload so every store/page re-fetches under the new
    // X-Account-Id header (mirrors the sidebar workspace switcher).
    window.location.assign('/dashboard');
  };
  const {
    pages,
    deletePage: deletePageFromStore,
    loadPages,
    loadPagesFast,
    loadPageStats,
    isLoadingPages,
    hasLoadedPages,
    isLoadingStats,
    createPage,
    setCurrentPage,
  } = useBuilderStore();
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [editSlugPage, setEditSlugPage] = useState<{ id: string; slug: string; name: string } | null>(null);
  const [renamingPageId, setRenamingPageId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [viewsGrowth, setViewsGrowth] = useState<{ percentage: number; isPositive: boolean } | null>(null);
  const [botStats, setBotStats] = useState<{ totalBlocked: number; blockedToday: number }>({ totalBlocked: 0, blockedToday: 0 });
  const [accountCustomDomains, setAccountCustomDomains] = useState<Array<{
    id: string;
    hostname: string;
    pageId: string;
    status: 'active' | 'pending' | 'failed';
  }>>([]);

  useEffect(() => {
    // Single fast load: the server now reads pre-computed unique counters
    // straight off landing_pages (the analytics writer maintains them via
    // the track_unique_visitor RPC), so /pages?fast=1 already returns
    // accurate numbers in ~50ms. We only fall back to the by-ids stats
    // endpoint if a row comes back without statsLoaded — that only
    // happens when running against an older deployment where the
    // pre-computed counters migration hasn't been applied yet.
    //
    // Depend on user.id (not the whole user object) so admin impersonation
    // always reloads for the new principal. Clear the previous identity's
    // rows immediately so we never flash the wrong user's table.
    if (!user?.id) return;
    let cancelled = false;
    useBuilderStore.getState().clearStore();
    (async () => {
      await loadPagesFast();
      if (cancelled) return;
      const needsStats = useBuilderStore
        .getState()
        .pages.some((p) => (p as any).statsLoaded === false);
      if (needsStats) {
        loadPageStats();
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id, loadPagesFast, loadPageStats]);

  // Fetch views growth data
  useEffect(() => {
    const fetchViewsGrowth = async () => {
      if (!user || pages.length === 0) return;
      
      try {
        const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
        const token = useAuthStore.getState().token;
        
        const response = await fetch(`${API_URL}/pages/stats/growth`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setViewsGrowth(data);
        }
      } catch (error) {
        console.error('Failed to fetch views growth:', error);
      }
    };

    fetchViewsGrowth();
  }, [user, pages]);

  // Fetch bot stats for user's pages
  useEffect(() => {
    const fetchBotStats = async () => {
      if (!user || pages.length === 0) return;
      try {
        const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
        const token = useAuthStore.getState().token;
        const pageIds = pages.map(p => p.id);
        
        // Fetch bot stats from user analytics endpoint
        const response = await fetch(`${API_URL}/pages/stats/bot-summary`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ pageIds }),
        });
        if (response.ok) {
          const data = await response.json();
          setBotStats(data);
        }
      } catch (error) {
        console.error('Failed to fetch bot stats:', error);
      }
    };
    fetchBotStats();
  }, [user, pages]);

  // Active custom domains for dashboard URLs — when a page has an active
  // hostname we show that instead of *.hitzlink.com (Open / Copy use it too).
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
        const res = await fetch(`${API_URL}/custom-domains`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        const rows = Array.isArray(data?.domains) ? data.domains : [];
        setAccountCustomDomains(rows.map((d: any) => ({
          id: String(d.id),
          hostname: String(d.hostname),
          pageId: String(d.pageId || ''),
          status: d.status === 'active' || d.status === 'failed' ? d.status : 'pending',
        })));
      } catch { /* swallow */ }
    })();
  }, [user]);

  const activeCustomDomainByPageId = useMemo(() => {
    const map = new Map<string, string>();
    for (const d of accountCustomDomains) {
      if (d.status === 'active' && d.pageId && !map.has(d.pageId)) {
        map.set(d.pageId, d.hostname);
      }
    }
    return map;
  }, [accountCustomDomains]);

  // Production app URL (hardcoded for reliability)
  const BASE_DOMAIN = 'hitzlink.com';

  // True only during the very first load of the page list — used to swap
  // the empty/zero state for skeletons so users with many pages don't see
  // a misleading "0 pages" screen while their data is on its way.
  const showInitialSkeleton = isLoadingPages && !hasLoadedPages;
  const showEmptyState = hasLoadedPages && pages.length === 0;
  // True while the server is still computing the heavy per-page analytics
  // aggregates. Used to render placeholder dashes/skeletons for the stat
  // columns so users know "still loading" vs "real value is 0".
  const statsArePending =
    isLoadingStats || pages.some((p) => (p as any).statsLoaded === false);

  const landingPages = pages.map(page => {
    const slug = (page as any).slug || page.id;
    const defaultUrl = `https://${slug}.${BASE_DOMAIN}`;
    const customHost = activeCustomDomainByPageId.get(page.id);
    return {
    id: page.id,
    name: page.name,
    url: customHost ? `https://${customHost}` : defaultUrl,
    defaultUrl,
    customDomain: customHost ?? null,
    views: (page as any).views || 0,
    uniqueVisitors: (page as any).uniqueVisitors || 0,
    uniqueClicks: (page as any).uniqueClicks || 0,
    conversions: (page as any).conversions || 0,
    statsLoaded: (page as any).statsLoaded !== false,
    isPublished: (page as any).is_published !== false, // Default to true if undefined
    autoPausedAt: ((page as any).auto_paused_at as string | null) ?? null,
    cloakingEnabled: Boolean((page.settings as any)?.cloaking?.enabled),
    lastUpdated: new Date(page.updatedAt).toLocaleDateString(undefined, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }),
    settings: page.settings,
  };
  });

  // Page limits come from the central PLAN_LIMITS table — never inline
  // per-tier numbers here, otherwise the next pricing change misses this
  // file. -1 means unlimited (Enterprise) and short-circuits the check.
  // Phase 4: prefer the effective cap (base + page_slots_extra) from
  // usePlanUsage so an addon purchase unlocks the Create button on the
  // next refresh without a page reload.
  const baseTierMax = getMaxPages(effectiveTier);
  const pageSlotsExtra = planUsage.addons?.pageSlotsExtra ?? 0;
  const pageLimit = baseTierMax === -1 ? -1 : baseTierMax + pageSlotsExtra;
  const canCreateMore = pageLimit === -1 || pages.length < pageLimit;

  const handleCreateNew = async () => {
    if (!canCreateMore) {
      const planName = tierLabel(effectiveTier); // "FREE" / "PRO" / "AGENCY"
      toast.error(t('limit.limitReachedToast', { plan: planName, count: pageLimit }));
      navigate('/upgrade');
      return;
    }
    
    try {
      const pageName = t('newPageDefaultName', { n: pages.length + 1 });
      const newPage = await createPage(pageName);
      if (newPage) {
        setCurrentPage(newPage);
        navigate('/builder');
        toast.success(t('toast.createSuccess'));
      }
    } catch (error: any) {
      toast.error(error.message || t('toast.createFailed'));
    }
  };

  const handleEdit = (pageId: string) => {
    const page = pages.find(p => p.id === pageId);
    if (page) {
      setCurrentPage(page);
      navigate('/builder');
    }
  };

  const handleDelete = (pageId: string) => {
    setDeleteConfirm(pageId);
  };

  const confirmDelete = async () => {
    if (deleteConfirm) {
      try {
        await deletePageFromStore(deleteConfirm);
        toast.success(t('toast.deleteSuccess'));
        setDeleteConfirm(null);
      } catch (error) {
        toast.error(t('toast.deleteFailed'));
      }
    }
  };

  const handleDuplicate = async (pageId: string) => {
    const page = pages.find(p => p.id === pageId);
    if (!page) {
      toast.error(t('toast.pageNotFound'));
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

      const newName = `${page.name} ${t('duplicateSuffix')}`;
      const newSettings = {
        ...page.settings,
        meta: {
          ...page.settings?.meta,
          title: newName,
        },
      };

      // Create duplicated page via API
      const response = await fetch(`${API_URL}/pages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: newName,
          components: page.components,
          settings: newSettings,
          published: false,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t('toast.duplicateFailed'));
      }

      await loadPages();
      toast.success(t('toast.duplicateSuccess'));
    } catch (error: any) {
      console.error('Duplicate error:', error);
      toast.error(error.message || t('toast.duplicateFailed'));
    }
  };

  const handleStartRename = (pageId: string, currentName: string) => {
    setRenamingPageId(pageId);
    setRenameValue(currentName);
  };

  const handleCancelRename = () => {
    setRenamingPageId(null);
    setRenameValue('');
  };

  const handleSaveRename = async (pageId: string) => {
    const trimmed = renameValue.trim();
    if (!trimmed) {
      toast.error(t('toast.renameEmpty'));
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

      const page = pages.find(p => p.id === pageId);
      const updatedSettings = {
        ...page?.settings,
        meta: {
          ...page?.settings?.meta,
          title: trimmed,
        },
      };

      const response = await fetch(`${API_URL}/pages/${pageId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: trimmed,
          settings: updatedSettings,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t('toast.renameFailed'));
      }

      await loadPages();
      toast.success(t('toast.renameSuccess'));
    } catch (error: any) {
      console.error('Rename error:', error);
      toast.error(error.message || t('toast.renameFailed'));
    } finally {
      setRenamingPageId(null);
      setRenameValue('');
    }
  };

  const handleCopyLink = (url: string) => {
    navigator.clipboard.writeText(url);
    toast.success(t('toast.linkCopied'));
  };

  const handleEditSlug = (pageId: string) => {
    const page = pages.find(p => p.id === pageId);
    if (page) {
      setEditSlugPage({ 
        id: page.id, 
        slug: (page as any).slug || page.id, 
        name: page.name 
      });
    }
  };

  const handleSlugUpdated = () => {
    // Close modal first
    setEditSlugPage(null);
    // Reload pages to get updated slug
    loadPages();
  };

  const handleViewAnalytics = (pageId: string) => {
    navigate(`/analytics?page=${pageId}`);
  };

  const handleTogglePublish = async (pageId: string, currentStatus: boolean) => {
    try {
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
      const token = useAuthStore.getState().token;
      
      const response = await fetch(`${API_URL}/pages/${pageId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          is_published: !currentStatus,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update page status');
      }

      await loadPages();
      toast.success(!currentStatus ? t('toast.publishOn') : t('toast.publishOff'));
    } catch (error) {
      console.error('Toggle publish error:', error);
      toast.error(t('toast.publishFailed'));
    }
  };

  // Cloaking is an Agency/Enterprise feature — only surface the per-row
  // toggle when the current plan actually includes it. The server-side
  // checkCloakingAccess gate is still the backstop on the PUT below.
  const cloakingAvailable = hasFeature(effectiveTier, 'cloaking');

  const handleToggleCloaking = async (pageId: string) => {
    // Read the page's existing cloaking object so we flip ONLY `enabled`
    // and preserve every other cloaking sub-setting (safe-page URL, rules,
    // mode). The PUT below shallow-merges settings at the top level, which
    // would otherwise replace the whole cloaking object with our partial.
    const target = pages.find((p) => p.id === pageId);
    const currentCloaking = ((target?.settings as any)?.cloaking) || {
      enabled: false,
      safePageUrl: 'https://www.google.com',
    };
    const newEnabled = !currentCloaking.enabled;
    try {
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
      const token = useAuthStore.getState().token;

      const response = await fetch(`${API_URL}/pages/${pageId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          settings: { cloaking: { ...currentCloaking, enabled: newEnabled } },
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update cloaking');
      }

      await loadPagesFast();
      toast.success(newEnabled ? t('toast.cloakingOn') : t('toast.cloakingOff'));
    } catch (error) {
      console.error('Toggle cloaking error:', error);
      toast.error(t('toast.cloakingFailed'));
    }
  };

  const totalViews = landingPages.reduce((sum, page) => sum + page.views, 0);
  const totalUniqueVisitors = landingPages.reduce((sum, page) => sum + page.uniqueVisitors, 0);
  const totalUniqueClicks = landingPages.reduce((sum, page) => sum + page.uniqueClicks, 0);
  const _totalConversions = landingPages.reduce((sum, page) => sum + page.conversions, 0);
  void _totalConversions;
  // Conversion Rate = Unique Clicks / Unique Visitors * 100
  const avgConversionRate = totalUniqueVisitors > 0 
    ? ((totalUniqueClicks / totalUniqueVisitors) * 100).toFixed(1)
    : '0.0';
  const uniquePercentage = totalViews > 0 
    ? ((totalUniqueVisitors / totalViews) * 100).toFixed(1) 
    : '0.0';

  const token = useAuthStore((state) => state.token);

  return (
    <UserLayout>
      <div className="p-4 sm:p-6 lg:p-8">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6 sm:mb-8">
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{t('header.title')}</h1>
            <p className="text-gray-600 mt-1 flex items-center gap-2 text-sm sm:text-base">
              {showInitialSkeleton ? (
                <>
                  <span className="inline-block h-4 w-32 animate-pulse rounded bg-gray-200" />
                  <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500" />
                </>
              ) : pageLimit === -1 ? (
                // Enterprise / unlimited — no quota line, just a tally.
                t('header.createdCount', { count: pages.length })
              ) : (
                // Every other tier (Free/Pro/Agency) shows live X/Y so
                // users see exactly how close they are to the cap. The
                // hardcoded "/3" in the old string was a tier-renaming
                // landmine — `max` is now sourced from PLAN_LIMITS.
                t('header.usage', {
                  used: pages.length,
                  max: pageLimit,
                  defaultValue: t('header.freeUsage', { used: pages.length, max: pageLimit }),
                })
              )}
            </p>
          </div>
          <button
            onClick={handleCreateNew}
            disabled={!canCreateMore}
            className={`flex items-center justify-center gap-2 px-4 sm:px-6 py-3 rounded-lg font-medium transition-all w-full sm:w-auto ${
              canCreateMore
                ? 'bg-blue-600 hover:bg-blue-700 text-white hover:shadow-lg'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            <Plus className="w-5 h-5" />
            <span className="text-sm sm:text-base">{t('header.createNew')}</span>
          </button>
        </div>

        {/* Phase 5: subscription lifecycle (renewal / payment failure /
            grandfather window / pages-auto-paused). Mounted ABOVE the
            email-verification banner so a payment-failed nudge is the
            first thing the user sees on login. */}
        <SubscriptionLifecycleBanner />

        {/* Email Verification Banner */}
        {token && <EmailVerificationBanner token={token} />}

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4 mb-6 sm:mb-8">
          <div className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-gray-600">{t('stats.totalPages')}</p>
              <FileText className="w-5 h-5 text-blue-500" />
            </div>
            {showInitialSkeleton ? (
              <div className="h-7 w-12 animate-pulse rounded bg-gray-200" />
            ) : (
              <p className="text-2xl font-bold text-gray-900">{pages.length}</p>
            )}
            {!showInitialSkeleton && (
              pageLimit === -1 ? (
                // Enterprise / unlimited — no cap to count down from.
                <p className="text-xs text-emerald-600 mt-1">
                  {t('stats.totalPagesUnlimited')}
                </p>
              ) : (
                <>
                  <p className="text-xs text-gray-500 mt-1">
                    {t('stats.totalPagesMax', { count: pageLimit })}
                  </p>
                  {/* How many more the user can still create. Clamped at 0
                      so an over-cap state (post-downgrade) reads "limit
                      reached" instead of a negative number. */}
                  {pageLimit - pages.length > 0 ? (
                    <p className="text-xs text-emerald-600 mt-0.5">
                      {t('stats.totalPagesRemaining', { count: pageLimit - pages.length })}
                    </p>
                  ) : (
                    <p className="text-xs text-orange-600 mt-0.5">
                      {t('stats.totalPagesRemainingFull')}
                    </p>
                  )}
                  {pageSlotsExtra > 0 && (
                    <p className="text-xs text-blue-600 mt-0.5">
                      {t('planUsage.addonContrib', {
                        value: pageSlotsExtra,
                        unit: t('stats.totalPages').toLowerCase(),
                      })}
                    </p>
                  )}
                </>
              )
            )}
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-gray-600">{t('stats.totalViews')}</p>
              <Eye className="w-5 h-5 text-green-500" />
            </div>
            {showInitialSkeleton || statsArePending ? (
              <div className="h-7 w-24 animate-pulse rounded bg-gray-200" />
            ) : (
              <p className="text-2xl font-bold text-gray-900">{totalViews.toLocaleString()}</p>
            )}
            {viewsGrowth !== null ? (
              <p className={`text-xs mt-1 flex items-center gap-1 ${
                viewsGrowth.isPositive ? 'text-green-600' : 'text-red-600'
              }`}>
                <TrendingUp className={`w-3 h-3 ${viewsGrowth.isPositive ? '' : 'rotate-180'}`} />
                {viewsGrowth.isPositive ? '+' : ''}{viewsGrowth.percentage.toFixed(1)}%
              </p>
            ) : (
              <p className="text-xs text-gray-400 mt-1">{t('stats.noDataYet')}</p>
            )}
          </div>

          <div className="bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl p-5 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-indigo-700">{t('stats.uniqueVisitors')}</p>
              <Users className="w-5 h-5 text-indigo-500" />
            </div>
            {showInitialSkeleton || statsArePending ? (
              <div className="h-7 w-24 animate-pulse rounded bg-indigo-100" />
            ) : (
              <p className="text-2xl font-bold text-gray-900">{totalUniqueVisitors.toLocaleString()}</p>
            )}
            <p className="text-xs text-indigo-600 mt-1">{t('stats.uniquePercentOfViews', { percent: uniquePercentage })}</p>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-gray-600">{t('stats.conversionRate')}</p>
              <TrendingUp className="w-5 h-5 text-green-500" />
            </div>
            {showInitialSkeleton || statsArePending ? (
              <div className="h-7 w-16 animate-pulse rounded bg-gray-200" />
            ) : (
              <p className="text-2xl font-bold text-gray-900">{avgConversionRate}%</p>
            )}
            <p className="text-xs text-gray-500 mt-1">{t('stats.conversionDetail', { clicks: totalUniqueClicks, visitors: totalUniqueVisitors })}</p>
          </div>

          <div className="bg-gradient-to-br from-red-50 to-orange-50 border border-red-200 rounded-xl p-5 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-red-700">{t('stats.botsBlocked')}</p>
              <ShieldCheck className="w-5 h-5 text-red-500" />
            </div>
            {showInitialSkeleton ? (
              <div className="h-7 w-24 animate-pulse rounded bg-red-100" />
            ) : (
              <p className="text-2xl font-bold text-gray-900">{botStats.totalBlocked.toLocaleString()}</p>
            )}
            <p className="text-xs text-red-600 mt-1">{t('stats.blockedToday', { count: botStats.blockedToday })}</p>
          </div>

          {(() => {
            // Plan card colour + label both come from the same normalised
            // tier so a future tier addition only needs one switch updated.
            // In a team workspace this is the OWNER's plan (effectiveTier).
            const tier = effectiveTier;
            const cardClasses: Record<typeof tier, string> = {
              free:       'bg-gradient-to-br from-yellow-50 to-orange-50 border-orange-200',
              pro:        'bg-gradient-to-br from-blue-50 to-purple-50 border-blue-200',
              agency:     'bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200',
              enterprise: 'bg-gradient-to-br from-gray-100 to-slate-200 border-slate-300',
            };
            const crownColor: Record<typeof tier, string> = {
              free:       'text-orange-600',
              pro:        'text-blue-600',
              agency:     'text-purple-600',
              enterprise: 'text-slate-700',
            };
            const planLabelKey: Record<typeof tier, string> = {
              free:       'stats.planFree',
              pro:        'stats.planPro',
              agency:     'stats.planAgency',
              enterprise: 'stats.planEnterprise',
            };

            // Plan-status hint line. Branches by tier + expiry:
            //   - free: nudge to upgrade
            //   - paid + expired: red "expired" (treat as urgent — they thought they were paid)
            //   - paid + ≤ 7 days left: amber "X days left" (renewal window)
            //   - paid + > 7 days left: neutral "expires <date>"
            //   - paid + no expiry: lifetime grant (admin-set)
            const renderHint = () => {
              if (!user) return null;
              // The expiry/upgrade hint is about the logged-in member's
              // OWN billing. Inside a team workspace the plan shown is the
              // owner's, so suppress the member-specific hint to avoid
              // mixing the owner's tier with the member's renewal date.
              if (inTeamWorkspace) return null;
              if (tier === 'free') {
                return (
                  <p className="text-xs text-orange-600 mt-1 font-medium">
                    {t('stats.upgradeArrow')}
                  </p>
                );
              }
              const exp = user.subscriptionExpiresAt;
              if (!exp) {
                return (
                  <p className="text-xs text-gray-500 mt-1">
                    {t('stats.planLifetime')}
                  </p>
                );
              }
              const expiryDate = new Date(exp);
              const dateLabel = expiryDate.toLocaleDateString(
                i18n.language === 'th' ? 'th-TH' : 'en-US',
                { day: 'numeric', month: 'short', year: 'numeric' }
              );
              const daysLeft = Math.ceil(
                (expiryDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24)
              );
              if (daysLeft < 0) {
                return (
                  <p className="text-xs text-red-600 mt-1 font-medium">
                    {t('stats.planExpired', { date: dateLabel })}
                  </p>
                );
              }
              if (daysLeft <= 7) {
                return (
                  <p className="text-xs text-amber-600 mt-1 font-medium">
                    {t('stats.planExpiringSoon', { days: daysLeft, date: dateLabel })}
                  </p>
                );
              }
              return (
                <p className="text-xs text-gray-500 mt-1">
                  {t('stats.planExpiresOn', { date: dateLabel })}
                </p>
              );
            };

            return (
              <div
                className={`${cardClasses[tier]} border rounded-xl p-5 hover:shadow-lg transition-shadow cursor-pointer`}
                onClick={() => navigate('/upgrade')}
              >
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-gray-700">{t('stats.yourPlan')}</p>
                  <Crown className={`w-5 h-5 ${crownColor[tier]}`} />
                </div>
                <p className="text-2xl font-bold text-gray-900">
                  {t(planLabelKey[tier], { defaultValue: tierLabel(effectiveTier) })}
                </p>
                {renderHint()}
              </div>
            );
          })()}
        </div>

        {/* Teams the user has joined — quick-switch cards so a member can
            jump straight into a workspace they collaborate in without
            opening the sidebar switcher. Only rendered when there is at
            least one joined team. */}
        {joinedTeams.length > 0 && (
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900">
              {t('planUsage.teamsTitle', { defaultValue: "Teams you've joined" })}
            </h2>
            <p className="text-sm text-gray-500 mb-3">
              {t('planUsage.teamsSubtitle', {
                defaultValue: 'Jump into a workspace you collaborate in',
              })}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {joinedTeams.map((team) => {
                const isActive = team.id === activeAccountId;
                const initial = (team.name || team.email || '?')
                  .charAt(0)
                  .toUpperCase();
                const roleLabel =
                  team.role === 'viewer'
                    ? t('planUsage.teamsRoleViewer', { defaultValue: 'Viewer' })
                    : t('planUsage.teamsRoleEditor', { defaultValue: 'Editor' });
                return (
                  <button
                    key={team.id}
                    type="button"
                    onClick={() => switchToWorkspace(team.id)}
                    className={`flex items-center gap-3 rounded-xl border p-4 text-left transition-all hover:shadow-lg ${
                      isActive
                        ? 'border-indigo-300 bg-indigo-50'
                        : 'border-gray-200 bg-white hover:border-indigo-200'
                    }`}
                  >
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 text-sm font-bold text-white">
                      {initial}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-gray-900">
                        {team.name || team.email || '—'}
                      </p>
                      <span className="mt-0.5 inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600">
                        {team.role === 'viewer' ? (
                          <Eye className="h-3 w-3" />
                        ) : (
                          <Pencil className="h-3 w-3" />
                        )}
                        {roleLabel}
                      </span>
                    </div>
                    <ExternalLink
                      className={`h-4 w-4 shrink-0 ${
                        isActive ? 'text-indigo-500' : 'text-gray-400'
                      }`}
                    />
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Plan Usage — metered quotas (storage / form submissions / AI gens).
            Renders unconditionally so first-time users (zero pages) still
            see what their plan caps are. The page-count meter already
            lives in the 6-stat grid above; this section covers the OTHER
            three quotas surfaced by /pages/limits + /safe-pages/ai/usage.
            Cards become clickable and route to /upgrade only when the
            user is at/over a cap (or AI is locked at their tier) — we
            don't want to nudge happy users to the pricing page. */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">{t('planUsage.title')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <PlanUsageCustomDomainCard usage={planUsage} navigate={navigate} t={t} />
            {/* Seat management is owner-only; the /team/members endpoint
                403s for members and would render a misleading "requires
                Pro or higher" card, so hide it inside a team workspace. */}
            {!inTeamWorkspace && (
              <PlanUsageSeatCard usage={planUsage} navigate={navigate} t={t} />
            )}
            <PlanUsageAiCard usage={planUsage} navigate={navigate} t={t} i18nLang={i18n.language} />
          </div>
        </div>

        {/* Page Limit Warning */}
        {!canCreateMore && (
          <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Lock className="w-5 h-5 text-orange-600" />
              <div>
                <p className="font-semibold text-orange-900">{t('limit.title')}</p>
                <p className="text-sm text-orange-700">
                  {/* Single tier-agnostic message — the upgrade page
                      tells the full comparison story. Falls back to the
                      old freeBody/plusBody keys if i18n hasn't been
                      reloaded yet (browsers with stale cache). */}
                  {t('limit.body', {
                    plan: tierLabel(effectiveTier),
                    count: pageLimit,
                    defaultValue:
                      effectiveTier === 'free'
                        ? t('limit.freeBody')
                        : t('limit.plusBody'),
                  })}
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate('/upgrade')}
              className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-gray-900 rounded-lg font-medium transition-colors"
            >
              {t('limit.upgradeNow')}
            </button>
          </div>
        )}

        {/* Pages Grid */}
        {showInitialSkeleton ? (
          // First-paint skeleton — shows while we fetch the metadata-only
          // page list (~200ms). Critical: previously the dashboard rendered
          // the empty state ("ยังไม่มีแลนดิ้งเพจ") while the slow stats
          // request was still in flight, which made users with 30+ pages
          // think they'd lost their data for 30 seconds.
          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            {Array.from({ length: 6 }).map((_, idx) => (
              <div
                key={idx}
                className={`flex items-center gap-5 px-5 py-3 ${
                  idx > 0 ? 'border-t border-gray-100' : ''
                }`}
              >
                <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-gray-200" />
                <div className="min-w-0 flex-1 space-y-2">
                  <div className="h-4 w-40 animate-pulse rounded bg-gray-200" />
                  <div className="h-3 w-56 animate-pulse rounded bg-gray-100" />
                </div>
                <div className="flex shrink-0 items-center gap-7 text-right text-xs">
                  <div className="space-y-1.5">
                    <div className="h-2.5 w-12 animate-pulse rounded bg-gray-100" />
                    <div className="h-4 w-14 animate-pulse rounded bg-gray-200" />
                  </div>
                  <div className="space-y-1.5">
                    <div className="h-2.5 w-12 animate-pulse rounded bg-gray-100" />
                    <div className="h-4 w-12 animate-pulse rounded bg-gray-200" />
                  </div>
                  <div className="space-y-1.5">
                    <div className="h-2.5 w-12 animate-pulse rounded bg-gray-100" />
                    <div className="h-4 w-10 animate-pulse rounded bg-gray-200" />
                  </div>
                </div>
                <div className="h-5 w-9 shrink-0 animate-pulse rounded-full bg-gray-200" />
                <div className="flex shrink-0 items-center gap-1">
                  {Array.from({ length: 5 }).map((__, i) => (
                    <div key={i} className="h-8 w-8 animate-pulse rounded-md bg-gray-100" />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : showEmptyState ? (
          <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
            <FileText className="w-16 h-16 text-gray-700 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('empty.title')}</h3>
            <p className="text-gray-600 mb-6">{t('empty.description')}</p>
            <button
              onClick={handleCreateNew}
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-gray-900 rounded-lg font-medium transition-colors"
            >
              <Plus className="w-5 h-5" />
              {t('empty.cta')}
            </button>
          </div>
        ) : (
          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            {landingPages.map((page, idx) => (
              <div
                key={page.id}
                className={`group flex flex-col gap-3 px-4 py-3 transition-colors hover:bg-gray-50 lg:flex-row lg:items-center lg:gap-5 lg:px-5 lg:py-3 ${
                  idx > 0 ? 'border-t border-gray-100' : ''
                }`}
              >
                {/* Status dot + name + url (left, flex-grow) */}
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  <span
                    className={`h-2.5 w-2.5 shrink-0 rounded-full ${
                      page.isPublished ? 'bg-green-500' : 'bg-gray-300'
                    }`}
                    title={page.isPublished ? t('row.statusActive') : t('row.statusInactive')}
                  />

                  <div className="min-w-0 flex-1">
                    {renamingPageId === page.id ? (
                      <div className="flex items-center gap-1">
                        <input
                          type="text"
                          value={renameValue}
                          onChange={(e) => setRenameValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveRename(page.id);
                            if (e.key === 'Escape') handleCancelRename();
                          }}
                          autoFocus
                          className="w-full rounded-md border border-blue-400 bg-white px-2 py-0.5 text-sm font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <button
                          type="button"
                          onClick={() => handleSaveRename(page.id)}
                          className="rounded-md p-1 text-green-600 hover:bg-green-50"
                          title={t('row.save')}
                        >
                          <Check className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={handleCancelRename}
                          className="rounded-md p-1 text-red-500 hover:bg-red-50"
                          title={t('row.cancel')}
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1">
                        <h3 className="truncate text-sm font-semibold text-gray-900">{page.name}</h3>
                        <button
                          type="button"
                          onClick={() => handleStartRename(page.id, page.name)}
                          className="rounded-md p-0.5 text-gray-400 opacity-0 transition-colors hover:bg-blue-50 hover:text-blue-600 group-hover:opacity-100"
                          title={t('row.rename')}
                        >
                          <Pencil className="h-3 w-3" />
                        </button>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <a
                        href={page.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="min-w-0 truncate text-blue-600 hover:underline"
                        title={
                          page.customDomain
                            ? `${page.url.replace(/^https?:\/\//, '')} (${page.defaultUrl.replace(/^https?:\/\//, '')})`
                            : page.url
                        }
                      >
                        {page.url.replace(/^https?:\/\//, '')}
                      </a>
                      <span className="hidden text-gray-300 sm:inline">·</span>
                      <span className="hidden whitespace-nowrap sm:inline">{page.lastUpdated}</span>
                    </div>
                  </div>
                </div>

                {/* Stats (compact, fixed widths) */}
                <div className="flex shrink-0 items-center gap-5 text-right text-xs sm:gap-7">
                  <div className="min-w-[64px]">
                    <p className="text-[10px] uppercase tracking-wide text-gray-500">{t('row.visitors')}</p>
                    {page.statsLoaded ? (
                      <p className="text-sm font-semibold text-indigo-600">
                        {page.uniqueVisitors.toLocaleString()}
                      </p>
                    ) : (
                      <div className="ml-auto h-4 w-12 animate-pulse rounded bg-indigo-100" />
                    )}
                  </div>
                  <div className="min-w-[56px]">
                    <p className="text-[10px] uppercase tracking-wide text-gray-500">{t('row.clicks')}</p>
                    {page.statsLoaded ? (
                      <p className="text-sm font-semibold text-purple-600">
                        {page.uniqueClicks.toLocaleString()}
                      </p>
                    ) : (
                      <div className="ml-auto h-4 w-10 animate-pulse rounded bg-purple-100" />
                    )}
                  </div>
                  <div className="min-w-[56px]">
                    <p className="text-[10px] uppercase tracking-wide text-gray-500">{t('row.conversion')}</p>
                    {page.statsLoaded ? (
                      <p className="text-sm font-semibold text-green-600">
                        {page.uniqueVisitors > 0
                          ? ((page.uniqueClicks / page.uniqueVisitors) * 100).toFixed(1)
                          : '0.0'}
                        %
                      </p>
                    ) : (
                      <div className="ml-auto h-4 w-10 animate-pulse rounded bg-green-100" />
                    )}
                  </div>
                </div>

                {/* Auto-paused badge — surfaced when downgradeEnforcement
                    flipped this page off. We deliberately do NOT lock the
                    publish toggle below: the user can still re-enable
                    manually, and the page-cap trigger on the server is
                    what blocks them past the cap. */}
                {page.autoPausedAt && (
                  <span
                    className="inline-flex shrink-0 items-center rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800 ring-1 ring-amber-200"
                    title={t('row.autoPausedTooltip')}
                  >
                    {t('row.autoPausedBadge')}
                  </span>
                )}

                {/* Cloaking toggle — only when the plan includes cloaking.
                    Shows at-a-glance ON/OFF state and flips it in place so
                    the operator doesn't have to open the Builder. */}
                {cloakingAvailable && (
                  <button
                    type="button"
                    onClick={() => handleToggleCloaking(page.id)}
                    className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-2 py-1 text-[10px] font-semibold ring-1 transition-colors ${
                      page.cloakingEnabled
                        ? 'bg-emerald-100 text-emerald-800 ring-emerald-200 hover:bg-emerald-200'
                        : 'bg-gray-100 text-gray-500 ring-gray-200 hover:bg-gray-200'
                    }`}
                    title={page.cloakingEnabled ? t('row.cloakingOn') : t('row.cloakingOff')}
                  >
                    <ShieldCheck className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">{t('row.cloaking')}</span>
                    <span
                      className={`relative inline-flex h-3.5 w-6 items-center rounded-full transition-colors ${
                        page.cloakingEnabled ? 'bg-emerald-600' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-2.5 w-2.5 transform rounded-full bg-white transition-transform ${
                          page.cloakingEnabled ? 'translate-x-3' : 'translate-x-0.5'
                        }`}
                      />
                    </span>
                  </button>
                )}

                {/* Publish toggle */}
                <button
                  type="button"
                  onClick={() => handleTogglePublish(page.id, page.isPublished)}
                  className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${
                    page.isPublished ? 'bg-green-600' : 'bg-gray-300'
                  }`}
                  title={page.isPublished ? t('row.togglePublishOn') : t('row.togglePublishOff')}
                >
                  <span
                    className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                      page.isPublished ? 'translate-x-5' : 'translate-x-1'
                    }`}
                  />
                </button>

                {/* Action icons — single compact row, wraps on narrow mobile screens */}
                <div className="flex shrink-0 flex-wrap items-center gap-1">
                  <button
                    type="button"
                    onClick={() => handleEdit(page.id)}
                    className="flex h-8 items-center gap-1 rounded-md bg-purple-600 px-2.5 text-xs font-medium text-white hover:bg-purple-700"
                    title={t('row.edit')}
                  >
                    <Edit className="h-3.5 w-3.5" />
                    {t('row.edit')}
                  </button>
                  <button
                    type="button"
                    onClick={() => window.open(page.url, '_blank')}
                    className="flex h-8 items-center gap-1 rounded-md bg-green-600 px-2.5 text-xs font-medium text-white hover:bg-green-700"
                    title={t('row.openLandingPage')}
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    {t('row.open')}
                  </button>

                  {/* Divider */}
                  <span className="mx-1 hidden h-5 w-px bg-gray-200 lg:inline-block" />

                  <button
                    type="button"
                    onClick={() => {
                      const pageData = pages.find((p) => p.id === page.id);
                      if (pageData) {
                        setCurrentPage(pageData);
                        navigate('/preview');
                      }
                    }}
                    className="flex h-8 w-8 items-center justify-center rounded-md text-gray-500 hover:bg-blue-50 hover:text-blue-700"
                    title={t('row.preview')}
                  >
                    <Eye className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleViewAnalytics(page.id)}
                    className="flex h-8 w-8 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                    title={t('row.analytics')}
                  >
                    <BarChart className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleEditSlug(page.id)}
                    className="flex h-8 w-8 items-center justify-center rounded-md text-gray-500 hover:bg-indigo-50 hover:text-indigo-700"
                    title={t('row.editUrl')}
                  >
                    <LinkIcon className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
                        const token = useAuthStore.getState().token;
                        const response = await fetch(`${API_URL}/pages/${page.id}`, {
                          headers: { Authorization: `Bearer ${token}` },
                        });
                        if (!response.ok) throw new Error('Failed to load page');
                        const freshPage = await response.json();
                        let components = freshPage.components || [];
                        if (typeof components === 'string') {
                          components = JSON.parse(components);
                        }
                        const spinWheelComponent = components.find((c: any) => c.type === 'spinWheel');
                        if (spinWheelComponent) {
                          navigate(`/spin-analytics/${page.id}/${spinWheelComponent.id}`);
                        } else {
                          toast.error(t('toast.spinNoComponent'));
                        }
                      } catch {
                        toast.error(t('toast.loadPageFailed'));
                      }
                    }}
                    className="hidden h-8 w-8 items-center justify-center rounded-md text-gray-500 hover:bg-orange-50 hover:text-orange-700 sm:flex"
                    title={t('row.spinAnalytics')}
                  >
                    <Disc className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleCopyLink(page.url)}
                    className="flex h-8 w-8 items-center justify-center rounded-md text-gray-500 hover:bg-teal-50 hover:text-teal-700"
                    title={t('row.copyLink')}
                  >
                    <Copy className="h-4 w-4" />
                  </button>

                  <span className="mx-1 hidden h-5 w-px bg-gray-200 lg:inline-block" />

                  <button
                    type="button"
                    onClick={() => handleDuplicate(page.id)}
                    className="flex h-8 w-8 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                    title={t('row.duplicate')}
                  >
                    <FileText className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(page.id)}
                    className="flex h-8 w-8 items-center justify-center rounded-md text-gray-500 hover:bg-red-50 hover:text-red-600"
                    title={t('row.delete')}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setDeleteConfirm(null)}>
          <div className="bg-white rounded-xl p-8 max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('delete.title')}</h2>
            <p className="text-gray-600 mb-6">
              {t('delete.description')}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
              >
                {t('delete.cancel')}
              </button>
              <button
                onClick={confirmDelete}
                className="px-5 py-2.5 bg-red-600 hover:bg-red-700 text-gray-900 rounded-lg font-medium transition-colors"
              >
                {t('delete.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Slug Modal */}
      {editSlugPage && (
        <EditSlugModal
          page={editSlugPage}
          isOpen={!!editSlugPage}
          onClose={() => setEditSlugPage(null)}
          onSuccess={handleSlugUpdated}
        />
      )}
    </UserLayout>
  );
};

/* -------------------------------------------------------------------------- */
/*                       Plan Usage cards (Phase 7)                           */
/* -------------------------------------------------------------------------- */

type CardCommonProps = {
  usage: PlanUsageState;
  navigate: NavigateFunction;
  t: TFunction;
};

/**
 * Three colour bands shared by every progress card. Aligns with the
 * existing dashboard convention (orange page-limit warning, red
 * "expired plan" hint) so users can read traffic-light state at
 * a glance: green = comfortable, amber = nearing cap, red = full.
 */
function progressBandColor(pct: number): { bar: string; track: string } {
  if (pct >= 80) return { bar: 'bg-red-500', track: 'bg-red-100' };
  if (pct >= 60) return { bar: 'bg-amber-500', track: 'bg-amber-100' };
  return { bar: 'bg-emerald-500', track: 'bg-emerald-100' };
}

function clampPct(used: number, cap: number): number {
  if (cap <= 0) return 0;
  return Math.min(100, Math.max(0, (used / cap) * 100));
}

const SkeletonValue: React.FC = () => (
  <div className="h-7 w-32 animate-pulse rounded bg-gray-200" />
);

const SkeletonBar: React.FC = () => (
  <div className="h-2 w-full animate-pulse rounded bg-gray-100 mt-3" />
);

const PlanUsageCustomDomainCard: React.FC<CardCommonProps> = ({ usage, navigate, t }) => {
  const isLoading = usage.isLoading || !usage.plan;
  const domains = usage.customDomains;
  // cap: -1 = unlimited, 0 = feature not on this plan (Free).
  const cap = domains?.cap ?? 0;
  const used = domains?.used ?? 0;
  const locked = cap === 0;
  const isUnlimited = cap === -1;
  const isFull = !locked && !isUnlimited && cap > 0 && used >= cap;
  const pct = isUnlimited ? 100 : clampPct(used, cap);
  const colors = isUnlimited ? { bar: 'bg-emerald-500', track: 'bg-emerald-100' } : progressBandColor(pct);
  const clickable = locked || isFull;

  const containerClass = locked
    ? 'bg-gray-50 border border-gray-200 rounded-xl p-5 hover:shadow-lg transition-shadow'
    : isFull
      ? 'bg-red-50 border border-red-200 rounded-xl p-5 hover:shadow-lg transition-shadow'
      : 'bg-white border border-gray-200 rounded-xl p-5 hover:shadow-lg transition-shadow';

  return (
    <div
      className={`${containerClass} ${clickable ? 'cursor-pointer' : ''}`}
      onClick={clickable ? () => navigate('/upgrade') : undefined}
      role={clickable ? 'button' : undefined}
    >
      <div className="flex items-center justify-between mb-2">
        <p className={`text-sm ${isFull ? 'text-red-700' : locked ? 'text-gray-500' : 'text-gray-600'}`}>
          {t('planUsage.customDomains')}
        </p>
        <Globe className={`w-5 h-5 ${isFull ? 'text-red-500' : locked ? 'text-gray-400' : 'text-sky-500'}`} />
      </div>

      {isLoading ? (
        <>
          <SkeletonValue />
          <SkeletonBar />
        </>
      ) : locked ? (
        <>
          <p className="text-base font-semibold text-gray-700">{t('planUsage.customDomainsLocked')}</p>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              navigate('/upgrade');
            }}
            className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-sky-600 hover:bg-sky-700 text-white text-xs font-medium transition-colors"
          >
            <Globe className="w-3.5 h-3.5" />
            {t('planUsage.lockedUpgrade')}
          </button>
        </>
      ) : isUnlimited ? (
        <>
          <p className="text-2xl font-bold text-gray-900">{used.toLocaleString()}</p>
          <p className="text-xs text-gray-500 mt-1">{t('planUsage.unlimited')}</p>
        </>
      ) : (
        <>
          <p className="text-2xl font-bold text-gray-900">
            {t('planUsage.usedSlash', { used: used.toLocaleString(), max: cap.toLocaleString() })}
          </p>
          <div className={`h-2 w-full rounded-full mt-3 ${colors.track}`}>
            <div
              className={`h-2 rounded-full transition-all ${colors.bar}`}
              style={{ width: `${pct}%` }}
            />
          </div>
          {isFull && (
            <p className="text-xs text-red-600 mt-2 font-medium">{t('planUsage.customDomainsFull')}</p>
          )}
        </>
      )}
    </div>
  );
};

const PlanUsageSeatCard: React.FC<CardCommonProps> = ({ usage, navigate, t }) => {
  const isLoading = usage.isLoading || !usage.plan;
  const seats = usage.seats;
  // max: -1 = unlimited, 0 = collaboration disabled (Free).
  const max = seats?.max ?? 0;
  const used = seats?.used ?? 0;
  const locked = max === 0;
  const isUnlimited = max === -1;
  const isFull = !locked && !isUnlimited && max > 0 && used >= max;
  const pct = isUnlimited ? 100 : clampPct(used, max);
  const colors = isUnlimited ? { bar: 'bg-emerald-500', track: 'bg-emerald-100' } : progressBandColor(pct);
  const clickable = locked || isFull;

  const containerClass = locked
    ? 'bg-gray-50 border border-gray-200 rounded-xl p-5 hover:shadow-lg transition-shadow'
    : isFull
      ? 'bg-red-50 border border-red-200 rounded-xl p-5 hover:shadow-lg transition-shadow'
      : 'bg-white border border-gray-200 rounded-xl p-5 hover:shadow-lg transition-shadow';

  return (
    <div
      className={`${containerClass} ${clickable ? 'cursor-pointer' : ''}`}
      onClick={clickable ? () => navigate('/upgrade') : undefined}
      role={clickable ? 'button' : undefined}
    >
      <div className="flex items-center justify-between mb-2">
        <p className={`text-sm ${isFull ? 'text-red-700' : locked ? 'text-gray-500' : 'text-gray-600'}`}>
          {t('planUsage.seats')}
        </p>
        <Users className={`w-5 h-5 ${isFull ? 'text-red-500' : locked ? 'text-gray-400' : 'text-indigo-500'}`} />
      </div>

      {isLoading ? (
        <>
          <SkeletonValue />
          <SkeletonBar />
        </>
      ) : locked ? (
        <>
          <p className="text-base font-semibold text-gray-700">{t('planUsage.seatsLocked')}</p>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              navigate('/upgrade');
            }}
            className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium transition-colors"
          >
            <Users className="w-3.5 h-3.5" />
            {t('planUsage.lockedUpgrade')}
          </button>
        </>
      ) : isUnlimited ? (
        <>
          <p className="text-2xl font-bold text-gray-900">{used.toLocaleString()}</p>
          <p className="text-xs text-gray-500 mt-1">{t('planUsage.unlimited')}</p>
        </>
      ) : (
        <>
          <p className="text-2xl font-bold text-gray-900">
            {t('planUsage.usedSlash', { used: used.toLocaleString(), max: max.toLocaleString() })}
          </p>
          <div className={`h-2 w-full rounded-full mt-3 ${colors.track}`}>
            <div
              className={`h-2 rounded-full transition-all ${colors.bar}`}
              style={{ width: `${pct}%` }}
            />
          </div>
          {isFull && (
            <p className="text-xs text-red-600 mt-2 font-medium">{t('planUsage.seatsFull')}</p>
          )}
        </>
      )}
    </div>
  );
};

const PlanUsageAiCard: React.FC<CardCommonProps & { i18nLang: string }> = ({
  usage,
  navigate,
  t,
  i18nLang,
}) => {
  // Keep showing skeleton until /pages/limits actually returned a plan —
  // otherwise we'd render a misleading "0 / 0" while the request is still
  // in flight (or errored). usage.plan is the post-fetch source of truth.
  const isLoading = usage.isLoading || !usage.plan;
  const ai = usage.ai;
  // AI access is gated PURELY by tier. Free/Pro never have it; Agency/
  // Enterprise always do. Earlier we also treated `!ai || ai.limit === 0`
  // as locked, but that mis-locked Enterprise/Agency users any time the
  // /safe-pages/ai/usage probe returned a transient error or its
  // graceful-fallback {plan:'free', limit:0} payload (see routes/
  // aiSafePages.js#usage catch block). The plan we trust is the one
  // /pages/limits returned, not the AI endpoint's self-report.
  const locked = usage.plan === 'free' || usage.plan === 'pro';
  const isUnlimited = ai?.unlimited === true;
  const used = ai?.used ?? 0;
  const limit = ai?.limit ?? 0;
  const isFull = !locked && !isUnlimited && limit > 0 && used >= limit;
  const pct = isUnlimited ? 100 : clampPct(used, limit);
  const colors = isUnlimited ? { bar: 'bg-emerald-500', track: 'bg-emerald-100' } : progressBandColor(pct);
  const clickable = locked || isFull;

  const containerClass = locked
    ? 'bg-gray-50 border border-gray-200 rounded-xl p-5 hover:shadow-lg transition-shadow'
    : isFull
      ? 'bg-red-50 border border-red-200 rounded-xl p-5 hover:shadow-lg transition-shadow'
      : 'bg-white border border-gray-200 rounded-xl p-5 hover:shadow-lg transition-shadow';

  const formattedReset = ai?.resetAt
    ? new Date(ai.resetAt).toLocaleDateString(i18nLang === 'th' ? 'th-TH' : 'en-US', {
        day: 'numeric',
        month: 'short',
      })
    : null;

  return (
    <div
      className={`${containerClass} ${clickable ? 'cursor-pointer' : ''}`}
      onClick={clickable ? () => navigate('/upgrade') : undefined}
      role={clickable ? 'button' : undefined}
    >
      <div className="flex items-center justify-between mb-2">
        <p className={`text-sm ${isFull ? 'text-red-700' : locked ? 'text-gray-500' : 'text-gray-600'}`}>
          {t('planUsage.aiGens')}
        </p>
        <Sparkles
          className={`w-5 h-5 ${
            isFull ? 'text-red-500' : locked ? 'text-gray-400' : 'text-fuchsia-500'
          }`}
        />
      </div>

      {isLoading ? (
        <>
          <SkeletonValue />
          <SkeletonBar />
        </>
      ) : locked ? (
        <>
          <p className="text-base font-semibold text-gray-700">{t('planUsage.aiLockedFreePro')}</p>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              navigate('/upgrade');
            }}
            className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-fuchsia-600 hover:bg-fuchsia-700 text-white text-xs font-medium transition-colors"
          >
            <Sparkles className="w-3.5 h-3.5" />
            {t('planUsage.aiLockedUpgrade')}
          </button>
        </>
      ) : isUnlimited ? (
        <>
          <p className="text-2xl font-bold text-gray-900">∞</p>
          <p className="text-xs text-gray-500 mt-1">{t('planUsage.unlimited')}</p>
        </>
      ) : (
        <>
          <p className="text-2xl font-bold text-gray-900">
            {t('planUsage.usedSlash', { used, max: limit })}
          </p>
          <div className={`h-2 w-full rounded-full mt-3 ${colors.track}`}>
            <div
              className={`h-2 rounded-full transition-all ${colors.bar}`}
              style={{ width: `${pct}%` }}
            />
          </div>
          {formattedReset && (
            <p className="text-xs text-gray-500 mt-2">
              {t('planUsage.resetsOn', { date: formattedReset })}
            </p>
          )}
        </>
      )}
    </div>
  );
};
